import { Button, EmptyState, Skeleton } from "@design";
import { Inbox } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { Page } from "@/components/Page";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { spaceCrumb } from "@/features/spaces/spaceCrumb";
import { ImportGroup, type ImportGroupInfo, RunningImports } from "../import/ImportGroup";
import { isRunning, useImports } from "../import/useImports";
import { AcceptProposalModal } from "./AcceptProposalModal";
import { ProposalCard } from "./ProposalCard";
import { type Proposal, useInbox, useRejectProposal, useSpaceName } from "./useInbox";

/** Chat proposals first (as one list), then one group per imported document, in inbox order. */
function groupBySource(proposals: Proposal[]): { chat: Proposal[]; imports: [ImportGroupInfo, Proposal[]][] } {
  const chat: Proposal[] = [];
  const imports = new Map<string, [ImportGroupInfo, Proposal[]]>();
  for (const p of proposals) {
    if (!p.importUuid) {
      chat.push(p);
      continue;
    }
    const entry = imports.get(p.importUuid) ?? [
      { importUuid: p.importUuid, filename: p.importFilename ?? "", spaceUuid: p.chatSpaceUuid ?? "", count: 0 },
      [],
    ];
    entry[0].count += 1;
    entry[1].push(p);
    imports.set(p.importUuid, entry);
  }
  return { chat, imports: [...imports.values()] };
}

/**
 * The knowledge inbox: facts suggested from the user's own chats and from
 * documents they imported (docs/design/knowledge-graph/KNOWLEDGE_GRAPH_DESIGN.md §6.2, §6.4). Only they
 * see these; accepting writes the fact into a wiki they choose, dismissing
 * means it's never suggested again. An import's facts are grouped under the
 * document, with bulk accept / dismiss.
 */
export function InboxPage() {
  const { t } = useTranslation("knowledge");
  const inbox = useInbox();
  const { knowledgeExtractionEnabled } = useCapabilities();
  const imports = useImports(undefined, { enabled: knowledgeExtractionEnabled });
  const reject = useRejectProposal();
  const [accepting, setAccepting] = useState<Proposal | null>(null);
  const [params, setParams] = useSearchParams();
  const spaceUuid = params.get("space");
  const spaceName = useSpaceName(spaceUuid);
  const proposals = (inbox.data?.proposals ?? []).filter((p) => !spaceUuid || p.chatSpaceUuid === spaceUuid);
  const running = (imports.data ?? []).filter((i) => isRunning(i) && (!spaceUuid || i.spaceUuid === spaceUuid));

  const card = (proposal: Proposal) => (
    <ProposalCard
      key={proposal.uuid}
      proposal={proposal}
      onAccept={() => setAccepting(proposal)}
      onReject={() => reject.mutate({ path: { proposal_uuid: proposal.uuid } })}
      rejecting={reject.isPending && reject.variables?.path.proposal_uuid === proposal.uuid}
    />
  );

  let body: ReactNode;
  if (inbox.isPending) {
    body = <Skeleton className="h-48 w-full" />;
  } else if (inbox.isError) {
    body = (
      <Text role="alert" tone="error">
        {t("inbox.loadFailed")}
      </Text>
    );
  } else if (proposals.length === 0) {
    body =
      running.length > 0 ? null : (
        <EmptyState icon={<Inbox />} title={t("inbox.emptyTitle")} body={t("inbox.emptyBody")} />
      );
  } else {
    const { chat, imports: groups } = groupBySource(proposals);
    body = (
      <div className="flex flex-col gap-8">
        {chat.length > 0 && (
          <section aria-label={t("inbox.listLabel")} className="flex flex-col gap-3">
            {groups.length > 0 && (
              <Text as="h2" variant="h3">
                {t("inbox.chatGroup")}
              </Text>
            )}
            {chat.map(card)}
          </section>
        )}
        {groups.map(([group, items]) => (
          <ImportGroup key={group.importUuid} group={group}>
            {items.map(card)}
          </ImportGroup>
        ))}
      </div>
    );
  }

  return (
    <>
      {spaceUuid && (
        <PageBreadcrumbs
          trail={[spaceCrumb(spaceUuid, spaceName), { label: t("title"), to: `/spaces/${spaceUuid}/knowledge` }]}
          current={t("inbox.title")}
        />
      )}
      <Page title={t("inbox.title")} subtitle={t("inbox.subtitle")}>
        {spaceUuid && (
          <div className="flex flex-wrap items-center gap-2">
            <Text variant="body-sm" tone="secondary">
              {spaceName ? t("inbox.filteredTo", { space: spaceName }) : t("inbox.filteredToSpace")}
            </Text>
            <Button variant="text" size="sm" onClick={() => setParams({})}>
              {t("inbox.showAll")}
            </Button>
          </div>
        )}
        <RunningImports imports={running} />
        {body}
        {accepting && <AcceptProposalModal proposal={accepting} onClose={() => setAccepting(null)} />}
      </Page>
    </>
  );
}
